import * as React from 'react';
import { PasswordModalView, PasswordModalViewProps } from './PasswordModal.view';
import { RegisterOptions, useForm, UseFormReturn } from 'react-hook-form';
import { ValidationMessages } from '../../../constants/validation-messages';
import { useState } from 'react';
import { FormOptions, ValidationPatterns } from '../../../constants/form';
import { ApolloError } from '@apollo/client';
import { SquareButtonIconsEnum } from '../../../constants/square-button-icons';
import { useUserData } from '../../../hooks/useUserData';
import { remapApolloErrors } from '../../../helpers/remapApolloErrors';
import { showToast } from '../../../helpers/showToast';
import { ChangePasswordMutationFn, SetPasswordMutationFn } from '../../../models/graphql';

export type PasswordModalControllerProps = Omit<
  PasswordModalViewProps,
  | 'onSubmitChangePassword'
  | 'onSubmitSetPassword'
  | 'submitted'
  | 'passwordRules'
  | 'hasCurrentPassword'
  | 'currentPasswordButtonState'
  | 'newPasswordButtonState'
  | 'confirmPasswordButtonState'
  | 'currentShowToggle'
  | 'newPasswordShowToggle'
  | 'confirmPasswordShowToggle'
  | 'useFormInstance'
> & {
  changePasswordMutation: ChangePasswordMutationFn;
  setPasswordMutation: SetPasswordMutationFn;
};

export const PasswordModalController = (props: PasswordModalControllerProps): JSX.Element => {
  const { userData } = useUserData();
  const [submitted, setSubmitted] = useState<boolean>(false);
  const defaultFormValues = { password: '' };

  const useFormInstance: UseFormReturn = useForm({
    ...FormOptions,
    defaultValues: defaultFormValues
  });

  const [currentPasswordButtonState, setCurrentPasswordButtonState] = useState<
    SquareButtonIconsEnum.show | SquareButtonIconsEnum.hide
  >(SquareButtonIconsEnum.show);
  const [newPasswordButtonState, setNewPasswordButtonState] = useState<
    SquareButtonIconsEnum.show | SquareButtonIconsEnum.hide
  >(SquareButtonIconsEnum.show);
  const [confirmPasswordButtonState, setConfirmPasswordButtonState] = useState<
    SquareButtonIconsEnum.show | SquareButtonIconsEnum.hide
  >(SquareButtonIconsEnum.show);

  const onSubmitChangePassword = (data: { newPassword: string; oldPassword: string }) => {
    setSubmitted(true);
    props
      .changePasswordMutation({
        variables: { newPassword: data.newPassword, oldPassword: data.oldPassword, userId: userData?.id }
      })
      .catch(e => {
        const errorForRemapping = e as ApolloError;
        const mappedError = remapApolloErrors(errorForRemapping);
        showToast({
          summary: 'Error in updating your password',
          detail: mappedError,
          severity: 'error'
        });

        setSubmitted(false);
        // Process Error Messaging
      });
  };

  const onSubmitSetPassword = (data: { newPassword: string }) => {
    setSubmitted(true);
    props.setPasswordMutation({ variables: { newPassword: data.newPassword, userId: userData?.id } }).catch(e => {
      const errorForRemapping = e as ApolloError;
      const mappedError = remapApolloErrors(errorForRemapping);
      showToast({
        summary: 'Error in setting your password',
        detail: mappedError,
        severity: 'error'
      });

      setSubmitted(false);
      // Process Error Messaging
    });
  };

  const passwordRules: RegisterOptions = {
    required: ValidationMessages.password.required,
    pattern: {
      value: ValidationPatterns.password,
      message: ValidationMessages.password.invalid
    }
  };

  // show hide password on input filed
  const currentShowToggle = () => {
    setCurrentPasswordButtonState(prevState =>
      prevState === SquareButtonIconsEnum.show ? SquareButtonIconsEnum.hide : SquareButtonIconsEnum.show
    );
  };
  const newPasswordShowToggle = () => {
    setNewPasswordButtonState(prevState =>
      prevState === SquareButtonIconsEnum.show ? SquareButtonIconsEnum.hide : SquareButtonIconsEnum.show
    );
  };

  const confirmPasswordShowToggle = () => {
    setConfirmPasswordButtonState(prevState =>
      prevState === SquareButtonIconsEnum.show ? SquareButtonIconsEnum.hide : SquareButtonIconsEnum.show
    );
  };

  const viewProps: PasswordModalViewProps = {
    ...props,
    hasCurrentPassword: true,
    onSubmitChangePassword,
    onSubmitSetPassword,
    submitted,
    passwordRules,
    currentPasswordButtonState,
    newPasswordButtonState,
    confirmPasswordButtonState,
    currentShowToggle,
    newPasswordShowToggle,
    confirmPasswordShowToggle,
    useFormInstance
  };

  return <PasswordModalView {...viewProps} />;
};
